import { useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

type BuildWhereFromFilterValues = (any) => string;

export function usePaginatedQuery(
  gqlQuery: DocumentNode,
  buildWhereFromFilterValues: BuildWhereFromFilterValues,
  order: string,
  appStore: any,
  execFirstTime = true, // Φέρε τα δεδομένα αμέσως
  initFilterValues = undefined,
  historyReplace = true
): any {
  //
  /* ----------------- Route State -------------------------- */
  const location = useLocation<any>();
  const history = useHistory();
  // Στο state του routing αποθηκεύω:
  //    1. την τρέχουσα σελίδα του table pagination (pageIndex)
  //  & 2. τα values των φίλτρων (filterValues)

  const queryState: { readonly pageIndex: number; readonly filterValues: any } =
    {
      pageIndex: location?.state?.pageIndex,
      filterValues: location?.state?.filterValues ?? initFilterValues,
    };

  /* ------------------- Query ------------------------------- */
  const tablePageSize = appStore.pageSize;
  const [initPageIndex, setInitPageIndex] = useState(
    queryState?.pageIndex ?? 0
  );
  const [queryParams, setQueryParams] = useState({
    offset: tablePageSize * initPageIndex,
    limit: tablePageSize,
    order: order,
    where: buildWhereFromFilterValues(queryState?.filterValues),
  });
  const {
    loading,
    data: rawData,
    error,
    refetch,
  } = useQuery(gqlQuery, {
    variables: { ...queryParams },
    fetchPolicy: 'network-only',
    skip: !execFirstTime,
    // onCompleted: (data) => {
    //   console.log('data= ', data);
    // },
  });

  useEffect(() => {
    console.log('order changed = ', order);
    const newQueryParams = {
      ...queryParams,
      order: order,
    };
    //setQueryParams(newQueryParams);
    refetch(newQueryParams);
  }, [order]);

  const { data, recordCount: dataCount } =
    rawData && rawData !== null && rawData[Object.keys(rawData)[0]] !== null
      ? rawData[Object.keys(rawData)[0]]
      : { data: undefined, recordCount: undefined };

  const filtersChanged = useCallback(
    (values) => {
      console.log('filtersChanged', values);
      // Φτιάξε το where για το graphql
      const where = buildWhereFromFilterValues(values);

      // Σύνθεσε τα vars του graphql
      const newQueryParams = {
        ...queryParams,
        offset: 0,
        where: where,
        order: order,
      };
      setQueryParams(newQueryParams); // αποθήκευσε τα vars
      refetch(newQueryParams); // φερε τα δεδομένα απο τον σερβερ
      setInitPageIndex(0); // αρχικοποίησε το gridTable στην 1η σελίδα

      // Ενημέρωσε το State του Route
      if (historyReplace) {
        history.replace(location.pathname, {
          filterValues: values,
          pageIndex: 0,
        });
      } else {
        history.replace(location.pathname, {
          ...location.state,
          pageIndex: 0,
        });
      }
    },
    [
      buildWhereFromFilterValues,
      history,
      location.pathname,
      queryParams,
      refetch,
    ]
  );

  /* handle Pagination */
  const gridPageChanged = useCallback(
    (pageIndex, pageSize: number) => {
      console.log('gridPageChanged', pageIndex, pageSize);
      setInitPageIndex(pageIndex);
      console.log(
        `gridPageChanged and queryParams= ${JSON.stringify(
          queryParams
        )} and order= ${order}`
      );
      const newQueryParams = {
        ...queryParams,
        offset: pageSize * pageIndex,
        limit: pageSize,
      };
      setQueryParams(newQueryParams);
      refetch(newQueryParams);

      // Ενημέρωσε το State του Route
      const state = {
        ...location.state,
        pageIndex,
      };
      if (historyReplace) history.replace(location.pathname, state);
    },
    [history, location.pathname, location.state, queryParams, refetch]
  );

  return {
    loading,
    rawData,
    data,
    dataCount,
    error,
    refetch,
    queryParams,
    setQueryParams,
    initPageIndex,
    setInitPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
  };
}

